import { styled } from '@mui/material/styles';
import { Box, Paper, Typography } from '@mui/material';

// ─── Page shell ──────────────────────────────────────────────────────────────

export const PageWrapperStyled = styled('div', {
  name: 'AdminPanel_PageWrapper',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2, 0, 4),
}));

// ─── Header ──────────────────────────────────────────────────────────────────

export const PageHeaderStyled = styled(Paper, {
  name: 'AdminPanel_PageHeader',
})(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2.5),
  padding: theme.spacing(3, 4),
  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
  color: '#fff',
  borderRadius: theme.shape.borderRadius,
}));

export const HeaderIconWrapperStyled = styled(Box, {
  name: 'AdminPanel_HeaderIconWrapper',
})(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  borderRadius: '50%',
  backgroundColor: 'rgba(255,255,255,0.15)',
  flexShrink: 0,
  fontSize: '1.5rem',
  color: '#fff',
}));

export const HeaderTextStyled = styled(Box, {
  name: 'AdminPanel_HeaderText',
})(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}));

export const HeaderTitleStyled = styled(Typography, {
  name: 'AdminPanel_HeaderTitle',
})(() => ({
  fontWeight: 700,
  color: '#fff',
  lineHeight: 1.2,
}));

export const HeaderSubtitleStyled = styled(Typography, {
  name: 'AdminPanel_HeaderSubtitle',
})(() => ({
  color: 'rgba(255,255,255,0.75)',
}));

// ─── Tabs ─────────────────────────────────────────────────────────────────────

export const TabsContainerStyled = styled(Paper, {
  name: 'AdminPanel_TabsContainer',
})(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

// ─── Feature grid ─────────────────────────────────────────────────────────────

export const FeaturesGridStyled = styled('div', {
  name: 'AdminPanel_FeaturesGrid',
})(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(2.5),
  padding: theme.spacing(3),
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

// ─── Feature card ─────────────────────────────────────────────────────────────

export const FeatureCardStyled = styled(Paper, {
  name: 'AdminPanel_FeatureCard',
})<{ available?: boolean }>(({ theme, available }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  transition: 'box-shadow 0.18s ease, transform 0.18s ease',
  cursor: available ? 'pointer' : 'default',
  ...(available && {
    '&:hover': {
      boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
      transform: 'translateY(-2px)',
    },
  }),
  ...(!available && {
    opacity: 0.65,
  }),
}));

export const CardTopBandStyled = styled(Box, {
  name: 'AdminPanel_CardTopBand',
})<{ bandcolor: string }>(({ bandcolor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  backgroundColor: `${bandcolor}18`, // ~10% opacity
  borderBottom: `1px solid ${bandcolor}30`,
}));

export const CardIconCircleStyled = styled(Box, {
  name: 'AdminPanel_CardIconCircle',
})<{ bandcolor: string }>(({ bandcolor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: `${bandcolor}22`,
  color: bandcolor,
  fontSize: '1.1rem',
  flexShrink: 0,
}));

export const CardBodyStyled = styled(Box, {
  name: 'AdminPanel_CardBody',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  padding: theme.spacing(2),
  gap: theme.spacing(1),
}));

export const CardTitleStyled = styled(Typography, {
  name: 'AdminPanel_CardTitle',
})(() => ({
  fontWeight: 700,
  lineHeight: 1.3,
}));

export const CardDescriptionStyled = styled(Typography, {
  name: 'AdminPanel_CardDescription',
})(({ theme }) => ({
  color: theme.palette.text.secondary,
  flex: 1,
  // clamp to 3 lines so all cards are visually consistent
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

export const CardFooterStyled = styled(Box, {
  name: 'AdminPanel_CardFooter',
})(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(1.5, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));
